import React, { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import i18next from '../../../lang';
import { rootStore } from "../../../stores/RootStore";
import SignUpForm from '../../../components/SignUp';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { adminStyles } from '../../../theme/adminStyles';
import UserListView, { UserListViewRef } from '../../../components/UserListView';
import UserBulkUploadDialog, { BulkUserData, InvalidBulkUserData } from '../../../components/UserBulkUploadDialog';
import axios from 'axios';
import * as XLSX from 'xlsx';

// 他ファイルで使用している型・関数の再エクスポート
export { getLevelColor, getLevelShortLabel, getStatusColor, levelChipSx } from '../../../components/UserListView';
export type { User } from '../../../components/UserListView';

const UserManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { authStore, viewStatusStore } = rootStore;
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const initialStatus = urlSearchParams.get('status') || '';

  const [openSignUp, setOpenSignUp] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [bulkNormalUsers, setBulkNormalUsers] = useState<BulkUserData[]>([]);
  const [bulkInvalidUsers, setBulkInvalidUsers] = useState<InvalidBulkUserData[]>([]);
  const listViewRef = useRef<UserListViewRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSignUpClose = () => {
    setOpenSignUp(false);
    listViewRef.current?.reload();
  };

  // サンプルExcelダウンロード
  const handleDownloadSample = () => {
    const headers = [
      { key: 'brandName', label: `${i18next.t('user.brand_name')} *` },
      { key: 'organizationName', label: `${i18next.t('user.classroom_name')} *` },
      { key: 'id', label: `${i18next.t('common.id')} *` },
      { key: 'password', label: `${i18next.t('auth.password')} *` },
      { key: 'email', label: `${i18next.t('contact.email_label')} *` },
      { key: 'phone', label: i18next.t('contact.phone') },
      { key: 'fax', label: i18next.t('contact.fax') },
      { key: 'postalCode', label: `${i18next.t('address.postal_code')} *` },
      { key: 'prefecture', label: `${i18next.t('address.prefecture')} *` },
      { key: 'city', label: `${i18next.t('address.city')} *` },
      { key: 'streetAddress', label: `${i18next.t('address.detail_address')} *` },
      { key: 'building', label: i18next.t('address.building_name') },
      { key: 'aliasName', label: i18next.t('address.alias_label') },
    ];

    const headerRow = headers.map(h => h.label);
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow]);
    worksheet['!cols'] = headers.map(() => ({ wch: 20 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, i18next.t('user.bulk_upload'));
    XLSX.writeFile(workbook, `classroom_bulk_register_sample.xlsx`);
  };

  // Excelアップロード・パース
  const handleUploadUsers = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      viewStatusStore.setLoadingStatus({ open: true });
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

      const users: BulkUserData[] = [];
      for (let row = 1; row <= range.e.r; row++) {
        const getCell = (col: number) => {
          const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
          return cell?.v?.toString().trim() || '';
        };

        const brandName = getCell(0);
        const organizationName = getCell(1);
        const id = getCell(2);

        // 空行スキップ
        if (!organizationName && !id) continue;

        users.push({
          line: row + 1,
          brandName,
          organizationName,
          id,
          password: getCell(3),
          email: getCell(4),
          phone: getCell(5),
          fax: getCell(6),
          postalCode: getCell(7),
          prefecture: getCell(8),
          city: getCell(9),
          streetAddress: getCell(10),
          building: getCell(11),
          aliasName: getCell(12),
        });
      }

      if (users.length === 0) {
        alert(i18next.t('common.no_search_results'));
        return;
      }

      // バックエンド検証
      const response = await axios.post('/api/v1/auth/bulk-verify', users);
      setBulkNormalUsers(response.data.normalUsers || []);
      setBulkInvalidUsers(response.data.invalidUsers || []);
      setOpenBulkDialog(true);
    } catch (error) {
      console.error('Excel parse error:', error);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
      // ファイル入力リセット
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleBulkSuccess = () => {
    listViewRef.current?.reload();
  };

  return (
    <Container maxWidth={false} sx={adminStyles.fullWidthPageContainer}>
      {/* hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xlsx,.xls"
        onChange={handleFileChange}
      />

      <UserListView
        ref={listViewRef}
        actionLabel={i18next.t('common.view_details')}
        onAction={(userId) => navigate(`/admin/users/${userId}`)}
        statusEditable={true}
        showOrgHierarchy={true}
        storageKeyPrefix="userMgmt"
        showRoleFilter={true}
        initialStatusFilter={initialStatus}
        filterActions={authStore.user.level !== 'CLASSROOM_MANAGER' ? (
          <>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={handleDownloadSample}
              size="small"
              sx={{ flexShrink: 0, fontSize: '0.75rem' }}
            >
              {i18next.t('user_management.download_format')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={handleUploadUsers}
              size="small"
              sx={{ flexShrink: 0, fontSize: '0.75rem' }}
            >
              {i18next.t('user_management.upload_file')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={() => setOpenSignUp(true)}
              size="small"
              sx={{ ...adminStyles.actionButton as any, flexShrink: 0, fontSize: '0.75rem' }}
            >
              {i18next.t('common.add')}
            </Button>
          </>
        ) : undefined}
      />

      <Dialog
        open={openSignUp}
        onClose={handleSignUpClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            height: isMobile ? '100%' : '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0'
        }}>
          {i18next.t('auth.new_registration')}
          <IconButton
            aria-label="close"
            onClick={handleSignUpClose}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <SignUpForm onSuccess={handleSignUpClose} isDialog={true} onCancel={handleSignUpClose} />
        </DialogContent>
      </Dialog>

      {/* 一括登録検証ダイアログ */}
      <UserBulkUploadDialog
        open={openBulkDialog}
        onClose={() => setOpenBulkDialog(false)}
        normalUsers={bulkNormalUsers}
        invalidUsers={bulkInvalidUsers}
        onSuccess={handleBulkSuccess}
      />
    </Container>
  );
};

export default UserManagement;
